import { getSafeUrl } from '@core/utils';
import {
    Apple, BookOpen, ChevronRight, Gem, Hammer, LocateFixed, Pill, Shirt, ShoppingBasket,
    Smartphone, Sofa, Store, UtensilsCrossed, Zap,
} from 'lucide-react';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';

const PREMIUM = '#1F3A5C';
const BRAND = '#ED7829';

// Icon per store category, picked from the name (the API has no icon field).
const iconFor = (name: string): React.ReactNode => {
    const n = (name || '').toLowerCase();
    const c = 'w-4 h-4';
    if (/restaurant|hotel|food|cafe|bakery/.test(n)) return <UtensilsCrossed className={c} />;
    if (/kirana|grocery|super/.test(n)) return <ShoppingBasket className={c} />;
    if (/fashion|cloth|garment/.test(n)) return <Shirt className={c} />;
    if (/home|decor|appliance|furniture/.test(n)) return <Sofa className={c} />;
    if (/mobile|computer|electronic/.test(n)) return <Smartphone className={c} />;
    if (/fruit|vegetable/.test(n)) return <Apple className={c} />;
    if (/pharmacy|medical|medicine/.test(n)) return <Pill className={c} />;
    if (/hardware|construction/.test(n)) return <Hammer className={c} />;
    if (/book|stationery/.test(n)) return <BookOpen className={c} />;
    if (/jewel/.test(n)) return <Gem className={c} />;
    if (/electrical/.test(n)) return <Zap className={c} />;
    return <Store className={c} />;
};

type CategoryLite = { id: number; name: string };

export const storeCategoryHref = (c: CategoryLite) => `/store/category/${c.id}/${getSafeUrl(c.name)}`;

// Reads the saved location (set by the location popup / geolocation) after mount.
export const useSavedLocationName = (refreshKey?: unknown) => {
    const [name, setName] = useState<string | null>(null);
    useEffect(() => {
        try {
            setName(JSON.parse(localStorage.getItem('location') ?? 'null')?.Location?.name || null);
        } catch {
            setName(null);
        }
    }, [refreshKey]);
    return name;
};

type HeroProps = {
    title: string;
    subtitle?: string;
    crumbs: { label: string; href?: string }[];
    count?: number | null;
};

export const StoresHero: React.FC<HeroProps> = ({ title, subtitle, crumbs, count }) => (
    <section className="px-2">
        <div className="relative rounded-3xl text-white" style={{ background: `linear-gradient(135deg, ${PREMIUM} 0%, #16293f 55%, #0f1c2e 100%)` }}>
            <div aria-hidden="true" className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
                <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: BRAND }} />
            </div>
            <div className="relative p-5 md:p-8 flex flex-col gap-3">
                <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-white/60">
                    {crumbs.map((c, i) => (
                        <React.Fragment key={c.label}>
                            {i > 0 && <ChevronRight className="w-3 h-3" />}
                            {c.href ? <Link href={c.href} prefetch={false} className="no-underline" style={{ color: 'rgba(255,255,255,0.75)' }}>{c.label}</Link>
                                : <span className="text-white/90 font-semibold">{c.label}</span>}
                        </React.Fragment>
                    ))}
                </nav>
                <h1 className="text-2xl md:text-4xl font-bold tracking-tight font-manrope m-0">{title}</h1>
                {subtitle && <p className="text-white/70 text-sm md:text-base max-w-2xl m-0">{subtitle}</p>}
                {typeof count === 'number' && count > 0 && (
                    <p className="m-0 text-sm text-white/70">{count} store{count > 1 ? 's' : ''} found</p>
                )}
            </div>
        </div>
    </section>
);

type RailProps = { categories: CategoryLite[]; activeId?: number | null; allHref?: string };

// Horizontal category switcher. Real links (crawlable, shareable), active one highlighted.
export const StoreCategoryRail: React.FC<RailProps> = ({ categories, activeId = null, allHref = '/store' }) => {
    if (!categories?.length) return null;
    // Colors are inline on purpose: global.scss has `a:hover, a:visited { color: inherit }`, which beats a
    // utility class and turned the white label of the dark active pill black on hover / after a visit.
    const chip = (active: boolean) =>
        `inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold no-underline border border-solid transition-colors ${active ? 'border-transparent' : 'bg-white border-neutral-200 hover:border-neutral-400'}`;
    const chipStyle = (active: boolean): React.CSSProperties => (active ? { backgroundColor: PREMIUM, color: '#fff' } : { color: '#404040' });
    return (
        <nav aria-label="Store categories" className="px-2">
            <ul className="flex gap-2 overflow-x-auto pb-2 list-none p-0 m-0" style={{ scrollbarWidth: 'thin' }}>
                <li className="flex-shrink-0">
                    <Link href={allHref} prefetch={false} className={chip(activeId === null)} style={chipStyle(activeId === null)}
                        aria-current={activeId === null ? 'page' : undefined}>
                        <Store className="w-4 h-4" /> All stores
                    </Link>
                </li>
                {categories.map((c) => (
                    <li key={c.id} className="flex-shrink-0">
                        <Link href={storeCategoryHref(c)} prefetch={false} className={chip(activeId === c.id)}
                            style={chipStyle(activeId === c.id)}
                            aria-current={activeId === c.id ? 'page' : undefined}>
                            {iconFor(c.name)} {c.name}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

// Shown when we could not read the visitor's location: the list is still useful, just not distance-ranked.
export const LocationNotice: React.FC<{ onRetry: () => void; onPick: () => void }> = ({ onRetry, onPick }) => (
    <div className="mx-2 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-solid border-amber-200 bg-amber-50 px-4 py-3">
        <p className="m-0 text-sm text-amber-900">
            <span className="font-bold">Showing stores from across Jhattse.</span> Share your location to see the ones nearest to you first.
        </p>
        <div className="flex gap-2">
            <button type="button" onClick={onRetry}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white border-0 cursor-pointer hover:opacity-90"
                style={{ backgroundColor: BRAND }}>
                <LocateFixed className="w-3.5 h-3.5" /> Use my location
            </button>
            <button type="button" onClick={onPick}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-amber-900 bg-white border border-solid border-amber-300 cursor-pointer hover:bg-amber-100">
                Choose a city
            </button>
        </div>
    </div>
);

export const StoreGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-2" aria-busy="true" aria-label="Loading stores">
        {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="h-36 rounded-2xl bg-neutral-100 animate-pulse" />
        ))}
    </div>
);

export const StoresEmpty: React.FC<{ title: string; hint: string; cta?: { label: string; href: string } }> = ({ title, hint, cta }) => (
    <div className="mx-2 flex flex-col items-center text-center gap-3 rounded-2xl bg-white border border-solid border-neutral-100 shadow-sm py-14 px-6">
        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-neutral-100 text-neutral-400"><Store className="w-8 h-8" /></span>
        <h2 className="text-lg font-bold text-neutral-800 m-0">{title}</h2>
        <p className="text-sm text-neutral-500 m-0 max-w-md">{hint}</p>
        {cta && (
            <Link href={cta.href} prefetch={false} className="mt-1 inline-flex rounded-xl px-5 py-2.5 text-sm font-semibold no-underline hover:opacity-90" style={{ backgroundColor: BRAND, color: '#fff' }}>
                {cta.label}
            </Link>
        )}
    </div>
);
